import { CustomerSalesData, CustomerMonthlyData, TransactionDetail } from '../types/SalesReport';

const API_BASE_URL = '/api/reports/customer-sales/customer-sales-comparison';

class CustomerSalesComparisonService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('token');

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  }

  async getCustomerSalesComparison(
    years: number[],
    customerIds?: string[],
    salesTerritories?: string[],
    salesPersonIds?: string[],
    customDateRanges?: Array<{ from: string; to: string }>
  ): Promise<{
    success: boolean;
    data: {
      customerData: CustomerSalesData[];
      years?: number[];
      customDateRanges?: Array<{ from: string; to: string }>;
    };
  }> {
    const params = new URLSearchParams();

    if (customDateRanges && customDateRanges.length > 0) {
      // Use custom date ranges mode
      params.set('customDateRanges', JSON.stringify(customDateRanges));
    } else {
      // Use years mode
      params.set('years', years.join(','));
    }

    if (customerIds && customerIds.length > 0) {
      params.set('customerIds', customerIds.join(','));
    }
    if (salesTerritories && salesTerritories.length > 0) {
      params.set('salesTerritory', salesTerritories.join(','));
    }
    if (salesPersonIds && salesPersonIds.length > 0) {
      params.set('salesPersonIds', salesPersonIds.join(','));
    }

    return this.request<{
      success: boolean;
      data: {
        customerData: CustomerSalesData[];
        years?: number[];
        customDateRanges?: Array<{ from: string; to: string }>;
      };
    }>(`?${params.toString()}`);
  }

  async getCustomerMonthlySalesComparison(
    years: number[],
    customerIds?: string[],
    salesTerritories?: string[],
    salesPersonIds?: string[],
    customDateRanges?: Array<{ from: string; to: string }>
  ): Promise<{
    success: boolean;
    data: {
      monthlyData: CustomerMonthlyData[];
      years?: number[];
      customDateRanges?: Array<{ from: string; to: string }>;
    };
  }> {
    const params = new URLSearchParams();

    if (customDateRanges && customDateRanges.length > 0) {
      // Use custom date ranges mode
      params.set('customDateRanges', JSON.stringify(customDateRanges));
    } else {
      // Use years mode
      params.set('years', years.join(','));
    }

    if (customerIds && customerIds.length > 0) {
      params.set('customerIds', customerIds.join(','));
    }
    if (salesTerritories && salesTerritories.length > 0) {
      params.set('salesTerritory', salesTerritories.join(','));
    }
    if (salesPersonIds && salesPersonIds.length > 0) {
      params.set('salesPersonIds', salesPersonIds.join(','));
    }

    return this.request<{
      success: boolean;
      data: {
        monthlyData: CustomerMonthlyData[];
        years?: number[];
        customDateRanges?: Array<{ from: string; to: string }>;
      };
    }>(`/monthly?${params.toString()}`);
  }

  async exportToExcel(
    years: number[],
    customerIds?: string[],
    salesTerritories?: string[],
    salesPersonIds?: string[],
    customDateRanges?: Array<{ from: string; to: string }>
  ): Promise<Blob> {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();

    if (customDateRanges && customDateRanges.length > 0) {
      // Use custom date ranges mode
      params.set('customDateRanges', JSON.stringify(customDateRanges));
    } else {
      // Use years mode
      params.set('years', years.join(','));
    }

    if (customerIds && customerIds.length > 0) {
      params.set('customerIds', customerIds.join(','));
    }
    if (salesTerritories && salesTerritories.length > 0) {
      params.set('salesTerritory', salesTerritories.join(','));
    }
    if (salesPersonIds && salesPersonIds.length > 0) {
      params.set('salesPersonIds', salesPersonIds.join(','));
    }
    const url = `${API_BASE_URL}/export/excel?${params.toString()}`;

    console.log('🔍 Excel export URL:', url);

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    console.log('🔍 Excel export response status:', response.status);
    console.log('🔍 Excel export response headers:', response.headers);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
      console.error('🔍 Excel export error:', errorData);
      throw new Error(errorData.message || `Export failed with status: ${response.status}`);
    }

    const blob = await response.blob();
    console.log('🔍 Excel blob size:', blob.size);
    return blob;
  }

  downloadExcel(blob: Blob, filename?: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      filename || `customer-sales-comparison-${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async getCustomerMonthTransactions(
    customerId: string,
    year: number,
    month: number
  ): Promise<{
    success: boolean;
    data: TransactionDetail[];
  }> {
    const token = localStorage.getItem('token');
    const url = `/api/reports/sales/transactions/${customerId}/${year}/${month}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async getInvoiceLineItems(documentNumber: string): Promise<{
    success: boolean;
    data: Array<{
      item_number: string;
      item_description: string;
      quantity: number;
      unit_price: number;
      extended_price: number;
      product_name?: string;
    }>;
  }> {
    const token = localStorage.getItem('token');
    const url = `/api/reports/sales/invoice/${documentNumber}/line-items`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async getFilterOptions(): Promise<{
    success: boolean;
    data: { territories: string[]; salesPersonIds: string[] };
  }> {
    return this.request<{
      success: boolean;
      data: { territories: string[]; salesPersonIds: string[] };
    }>('/filter-options');
  }
}

export const customerSalesComparisonService = new CustomerSalesComparisonService();
